import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Linkedin } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { Tilt } from "@/components/Tilt";
import { Pinwheel } from "@/components/Pinwheel";
import { OrbitModel } from "@/components/OrbitModel";
import { Counter } from "@/components/Counter";
import { SectionHead } from "@/components/SectionHead";
import { ParticleSphere } from "@/components/ParticleSphere";
import { SEO } from "@/components/SEO";
import { SectorMark } from "@/components/SectorMark";
import { INDUSTRIES, COMPANIES, SEED_STATS, TIMELINE, LEADERS } from "@/data/content";

const IndustriesBento = () => {
    const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
    return (
        <div className="mt-16 grid gap-5 md:grid-cols-12" data-testid="industries-grid">
            {INDUSTRIES.map((ind, i) => (
                <Reveal key={ind.key} delay={i * 0.08} className={`${spans[i]} col-span-1`}>
                    <Tilt className="h-full">
                    <Link
                        to="/group"
                        data-testid={`industry-card-${ind.key}`}
                        className="group relative block h-[380px] overflow-hidden bg-[#0A0D20] md:h-[440px]"
                    >
                        <img
                            src={ind.image}
                            alt={ind.name}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D20]/95 via-[#0A0D20]/35 to-transparent" />
                        <ArrowUpRight
                            className="absolute right-6 top-6 h-6 w-6 text-white opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100"
                            strokeWidth={1.5}
                        />
                        <div className="absolute inset-x-0 bottom-0 p-8">
                            <span className="inline-block border border-[#F16622]/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F16622]">
                                {ind.tag}
                            </span>
                            <h3 className="font-display mt-4 text-2xl font-medium text-white md:text-3xl">{ind.name}</h3>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/65">{ind.desc}</p>
                            <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/45">
                                {ind.companies.join("  ·  ")}
                            </p>
                        </div>
                    </Link>
                    </Tilt>
                </Reveal>
            ))}
        </div>
    );
};

const CompaniesMarquee = () => {
    const tiles = [...COMPANIES, ...COMPANIES];
    return (
        <div className="logo-stage mt-16 overflow-hidden py-14" data-testid="companies-marquee">
            <div className="marquee-track flex w-max items-stretch gap-7 px-4" style={{ animationDuration: "52s" }}>
                {tiles.map((c, i) => (
                    <div
                        key={`${c.id}-${i}`}
                        className="logo-tile group relative flex h-52 w-80 shrink-0 flex-col overflow-hidden bg-white"
                        data-testid={`marquee-tile-${c.id}-${i < COMPANIES.length ? "a" : "b"}`}
                    >
                        <span
                            className="logo-tile__halo"
                            style={{ background: `radial-gradient(ellipse 75% 65% at 50% 42%, ${c.color}1f 0%, transparent 70%)` }}
                        />
                        <span className="logo-tile__sheen" />
                        <div className="flex flex-1 items-center justify-center px-10 pt-4">
                            {c.logo ? (
                                <img
                                    src={c.logo}
                                    alt={c.name}
                                    loading="lazy"
                                    className="logo-tile__img max-h-24 w-auto object-contain"
                                />
                            ) : (
                                <span className="font-display text-2xl font-semibold tracking-wide" style={{ color: c.color }}>
                                    7 <span className="font-light">SEEDS</span>
                                </span>
                            )}
                        </div>
                        <div className="flex items-center justify-between px-6 pb-4">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 transition-colors duration-500 group-hover:text-slate-600">
                                {c.sector}
                            </span>
                            <span
                                className="h-[3px] w-10 transition-all duration-500 group-hover:w-16"
                                style={{ backgroundColor: c.color }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LeaderCard = ({ leader, delay }) => (
    <Reveal delay={delay}>
        <article
            data-testid={`leader-card-${leader.id}`}
            className="group flex h-full flex-col border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-colors duration-500 hover:border-[#F16622]/60 md:p-7"
        >
            <div className="overflow-hidden">
                <img
                    src={leader.photo}
                    alt={leader.name}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                />
            </div>
            <blockquote className="mt-7 flex-1 text-[15px] leading-relaxed text-white/75">
                &ldquo;{leader.quote}&rdquo;
            </blockquote>
            <div className="mt-7 flex items-end justify-between gap-4">
                <div>
                    <p className="font-display text-lg font-medium text-white">{leader.name}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#F16622]">{leader.role}</p>
                </div>
                <a
                    href={leader.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${leader.name} on LinkedIn`}
                    data-testid={`leader-linkedin-${leader.id}`}
                    className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/20 text-white/70 transition-colors duration-300 hover:border-[#F16622] hover:text-[#F16622]"
                >
                    <Linkedin className="h-4 w-4" strokeWidth={1.5} />
                </a>
            </div>
            {leader.creds && (
                <div className="mt-6 bg-white p-3">
                    <img src={leader.creds} alt={`${leader.name} — academic credentials`} loading="lazy" className="w-full object-contain" />
                </div>
            )}
        </article>
    </Reveal>
);

export default function Home() {
    return (
        <main data-testid="home-page">
            <SEO
                title="MS Group | Hybrid Seeds, FMCG, Real Estate & Pharma — India"
                description="MS Group is a diversified Indian business group — My Seeds, Navayuga Seeds, Seven Seeds, Seed Park, Mega Foods, Navayuga Ventures and Navayuga Healthcare. Trusted by 1M+ farmers and 5,000+ dealers across India."
            />
            <Hero />

            {/* About intro */}
            <section className="bg-[#F8F7F3] py-14 md:py-20" data-testid="home-about-intro">
                <div className="mx-auto grid max-w-[1440px] items-center gap-16 px-6 md:px-12 lg:grid-cols-2 lg:px-20">
                    <div>
                        <SectionHead
                            overline="Who We Are"
                            title="Rooted in India. Growing across sectors."
                            sub="From agriculture to wellness, real estate to FMCG — MS Group builds purposeful ventures that touch everyday lives, empower communities, and help shape tomorrow's India."
                        />
                        <Reveal delay={0.2}>
                            <p className="mt-6 text-base leading-relaxed text-slate-600">
                                What began in 2014 as a single seed company is today a diversified group of seven
                                businesses. Each one is held to the same standard that built our name in the fields of
                                rural India: trust before transactions, and value that outlives quarters.
                            </p>
                        </Reveal>
                        <Reveal delay={0.28}>
                            <Link
                                to="/about"
                                data-testid="home-discover-story-link"
                                className="group mt-10 inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#232B6B] transition-colors hover:text-[#F16622]"
                            >
                                Discover our story
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="relative">
                        <div className="relative ml-auto w-full max-w-[520px]">
                            <div className="absolute -left-5 -top-5 h-full w-full border border-[#F16622]" />
                            <div className="relative h-[440px] w-full overflow-hidden border border-[#ECEAE3] bg-white md:h-[520px]" data-testid="about-sector-stage">
                                <div
                                    className="pointer-events-none absolute inset-0"
                                    style={{ background: "radial-gradient(ellipse 70% 55% at 50% 46%, rgba(241,102,34,0.08), transparent 70%)" }}
                                />
                                <SectorMark className="absolute inset-0" />
                                <p className="absolute bottom-5 right-6 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                                    One Mark · Four Industries
                                </p>
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-[#232B6B] px-8 py-6 text-white">
                                <p className="font-display text-3xl font-medium">2014</p>
                                <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-white/60">The year it began</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Group at a glance — seed enterprise stats */}
            <section className="grain relative overflow-hidden bg-[#0A0D20] py-14 md:py-20" data-testid="home-stats">
                <div className="pointer-events-none absolute -top-8 right-0 hidden select-none font-display text-[15rem] font-semibold leading-none text-outline lg:block">
                    2014
                </div>
                <div className="relative mx-auto grid max-w-[1440px] gap-16 px-6 md:px-12 lg:grid-cols-[1fr_1.6fr] lg:px-20">
                    <div>
                        <SectionHead
                            dark
                            overline="Group at a Glance"
                            title="A foundation a million farmers strong."
                            sub="These numbers belong to our seed enterprise — the business that built MS Group, and the roots from which every new venture grows."
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
                        {SEED_STATS.map((s, i) => (
                            <div key={s.label} className="bg-[#0A0D20] p-8 md:p-10" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                                <Reveal delay={i * 0.08}>
                                    <p className="font-display text-4xl font-medium tracking-tight text-white md:text-5xl">
                                        <Counter end={s.end} suffix={s.suffix} />
                                    </p>
                                    <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#F16622]">{s.label}</p>
                                    <p className="mt-2 text-sm text-white/45">{s.sub}</p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Industries */}
            <section className="bg-white py-14 md:py-20" data-testid="home-industries">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <div className="flex flex-wrap items-end justify-between gap-8">
                        <SectionHead
                            overline="Our Industries"
                            title="Four sectors. One standard of excellence."
                            sub="Agriculture gave us our foundation. Diversification is our future. Every sector we enter is chosen for long-term value and regional impact."
                        />
                        <Reveal delay={0.2}>
                            <Link
                                to="/group"
                                data-testid="industries-view-all-link"
                                className="group inline-flex items-center gap-3 border border-[#232B6B] px-7 py-3.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#232B6B] transition-colors duration-300 hover:bg-[#232B6B] hover:text-white"
                            >
                                View the portfolio
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                    <IndustriesBento />
                </div>
            </section>

            {/* Group companies — brand wall */}
            <section className="relative overflow-hidden bg-[#F8F7F3] py-14 md:py-20" data-testid="home-companies">
                <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <SectionHead
                        overline="Group Companies"
                        title="Seven businesses. One family."
                        sub="Each company carries its own identity — and the shared MS Group promise of trust, quality and long-term thinking."
                    />
                </div>
                <CompaniesMarquee />
                <div className="relative mx-auto mt-12 max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <Reveal>
                        <Link
                            to="/group"
                            data-testid="companies-explore-link"
                            className="group inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#232B6B] transition-colors hover:text-[#F16622]"
                        >
                            Explore each company
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Journey */}
            <section className="bg-white py-14 md:py-20" data-testid="home-journey">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <SectionHead
                        overline="Our Journey"
                        title="A decade of patient building."
                        sub="From one seed company in 2014 to a diversified group — every step deliberate, every venture earned."
                    />
                    <div className="relative mt-14">
                        <div className="absolute bottom-0 left-[7px] top-0 w-px bg-[#E4E2DA] md:left-1/2" />
                        {TIMELINE.map((t, i) => (
                            <Reveal key={t.year} delay={0.05}>
                                <div
                                    className={`relative mb-16 grid gap-4 pl-10 md:grid-cols-2 md:gap-0 md:pl-0 ${
                                        i % 2 === 1 ? "" : ""
                                    }`}
                                    data-testid={`timeline-${t.year}`}
                                >
                                    <span className="absolute left-0 top-2 h-4 w-4 rotate-45 border-2 border-[#F16622] bg-white md:left-1/2 md:-translate-x-1/2" />
                                    <div className={i % 2 === 0 ? "md:col-start-1 md:pr-20 md:text-right" : "md:col-start-2 md:pl-20"}>
                                        <div className={`flex items-center gap-3 ${i % 2 === 0 ? "md:justify-end" : ""}`}>
                                        <p className="font-display text-4xl font-medium tracking-tight text-[#F16622] md:text-5xl">{t.year}</p>
                                        {t.upcoming && (
                                            <span className="border border-[#F16622] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#F16622]" data-testid="timeline-upcoming-badge">
                                                Upcoming
                                            </span>
                                        )}
                                        </div>
                                        <h3 className="font-display mt-3 text-xl font-medium text-[#0F172A] md:text-2xl">{t.title}</h3>
                                        <p className="mt-3 max-w-md text-[15px] leading-relaxed text-slate-600 md:max-w-none">{t.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="grain relative bg-[#0A0D20] py-14 md:py-20" data-testid="home-leadership">
                <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <SectionHead
                        dark
                        overline="Leadership"
                        title="The people behind the vision."
                        sub="Globally educated, deeply rooted — a leadership team that pairs international learning with Indian ground truth."
                    />
                    <div className="mt-16 grid gap-6 md:grid-cols-3">
                        {LEADERS.map((l, i) => (
                            <LeaderCard key={l.id} leader={l} delay={i * 0.1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Philosophy */}
            <section className="relative overflow-hidden bg-white py-14 md:py-20" data-testid="home-philosophy">
                <Pinwheel className="pointer-events-none absolute -right-16 -top-16 opacity-[0.07]" size={380} />
                <Pinwheel className="pointer-events-none absolute -bottom-20 -left-20 opacity-[0.05]" size={300} />
                <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <div className="grid gap-16 lg:grid-cols-[1.3fr_1fr]">
                        <SectionHead
                            overline="Our Philosophy"
                            title="Built on Trust. Driven by Purpose."
                            sub="Every MS Group venture is rooted in responsibility — to the farmer, the consumer, the investor and the environment. We don't just build companies. We build relationships that last, and impact that matters."
                        />
                        <Reveal delay={0.15}>
                            <div className="flex h-full flex-col justify-center border-l-2 border-[#F16622] pl-8">
                                <p className="font-display text-2xl font-light leading-snug text-[#232B6B] md:text-3xl">
                                    &ldquo;We are not building for the next season. We are building institutions that
                                    outlive us.&rdquo;
                                </p>
                                <p className="mt-6 text-xs uppercase tracking-[0.25em] text-slate-500">
                                    The founding principle of MS Group
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Work approach band */}
            <section className="bg-[#D9A521] py-12 md:py-16" data-testid="home-work-approach">
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
                    <Reveal>
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#0A0D20]/60">Our Work Approach</p>
                        <p className="font-display mt-5 max-w-4xl text-2xl font-medium leading-snug text-white md:text-4xl">
                            Great companies aren't run by chance — they're built on clarity, people, and repeatable
                            systems.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Future vision + 3D */}
            <section className="grain relative overflow-hidden bg-[#0A0D20] py-14 md:py-20" data-testid="home-vision">
                <div className="relative mx-auto grid max-w-[1440px] items-center gap-12 px-6 md:px-12 lg:grid-cols-2 lg:px-20">
                    <div>
                        <SectionHead
                            dark
                            overline="The Road Ahead"
                            title="The next decade belongs to those who build for generations."
                            sub="We will deepen our leadership in seeds, scale Mega Foods into Indian households, develop spaces that appreciate in value and trust, and bring clinician-led healthcare to more communities — one deliberate step at a time."
                        />
                        <Reveal delay={0.2}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="vision-partner-btn"
                                    className="group inline-flex items-center gap-3 bg-[#F16622] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-[#0A0D20]"
                                >
                                    Partner with us
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/group"
                                    data-testid="vision-group-btn"
                                    className="inline-flex items-center gap-3 border border-white/30 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
                                >
                                    Meet the Group
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <ParticleSphere className="mx-auto h-[340px] w-full max-w-[520px] md:h-[460px]" />
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
